import React, { useState, useEffect, useRef, } from 'react';
import { FormGroup, Label, InputGroup, Input, Button, Modal, ModalBody, ModalFooter, Form, } from 'reactstrap';
import { isEmpty, sortBy, sum, groupBy, get, keyBy, omit, mapValues, } from 'lodash';
import { useDebounce, useToggle, useList, } from 'react-use';
import { toast, } from 'react-toastify';

import okSoundSrc from '../../sounds/ok.mp3';
import ngSoundSrc from '../../sounds/ng.mp3';
import firebase from '../../firebase';
import { getDocumentData } from '../../shared/firebase';
import { isMobile, safePrefix, } from '../../util';
import useFormState from '../hooks/useFormState';
import ProductSelectOptionLabel from '../ProductSelectOptionLabel';
import Field from '../Field';
import { idWithTenant, } from '../../shared/util';
import ListForm from '../ListForm';
import ProgressButton from '../ProgressButton';
import PendingItemForm from '../forms/PendingItemForm';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentSubscription from '../hooks/useDocumentSubscription';

const { entries, } = Object;
const db = firebase.firestore();
const inventoriesRef = db.collection('inventories');
const tenantsRef = db.collection('tenants');

export default function RevertingShelfModal(props) {
  const { isOpen, tenants, locations, onClickClose, } = props;
  const ngAudioEl = useRef(null);
  const okAudioEl = useRef(null);
  const locationsByKey = keyBy(locations, 'sortKey');
  const tenantsById = keyBy(tenants, 'id');
  const [locationKeyValue, setLocationKeyValue] = useState('');
  const matchedLocation = locationsByKey[locationKeyValue];
  const [productBarcodeInfoValue, setProductBarcodeInfoValue] = useState('');
  const [foundInventory, setFoundInventory] = useState(null);
  const matchedInventories = useCollectionSubscription(matchedLocation && inventoriesRef.where('status', '==', 'shelved').where('locationId', '==', matchedLocation.id), [locationKeyValue]);
  const foundProduct = useDocumentSubscription(foundInventory && tenantsRef.doc(foundInventory.tenantId).collection('products').doc(foundInventory.productId), [foundInventory?.id]);
  const productIdInput = useRef(null);
  const playAudio = (audio) => {
    audio.currentTime = 0;
    audio.play();
  };

  const handleMoveInventory = async () => {
    if(!foundInventory) return;

    const { tenantId, receivingRequestId, isNg, productId, } = foundInventory;
    const receivingRequest = receivingRequestId && await getDocumentData(tenantsRef.doc(tenantId).collection('receivingRequests').doc(receivingRequestId));
    const batch = db.batch();
    batch.update(foundInventory.ref, {
      locationId: null,
      status: 'received',
    });
    const inventoryType = isNg ? 'ng' : 'good';
    const inventoryIdsFieldName = inventoryType + 'InventoryIds';
    batch.update(tenantsRef.doc(tenantId).collection('products').doc(productId), {
      [inventoryIdsFieldName]: firebase.firestore.FieldValue.arrayRemove(foundInventory.id),
    });
    if(receivingRequest != null) {
      batch.set(receivingRequest.ref, {
        [[inventoryIdsFieldName, productId].join('-')]: firebase.firestore.FieldValue.arrayRemove(foundInventory.id),
        status: 'processing',
      }, { merge: true });
    }
    await batch.commit();

    toast.success('在庫を移動しました');
    setFoundInventory(null);
    setProductBarcodeInfoValue('');
    productIdInput.current.focus();
  };

  useDebounce(() => {
    if(isEmpty(productBarcodeInfoValue)) {
      setFoundInventory(null);
      return;
    }

    const inventory = sortBy(matchedInventories, _ => _.createdAt.toDate()).find(_ => _.productBarcodeInfo === safePrefix(productBarcodeInfoValue));
    if(inventory == null) {
      playAudio(ngAudioEl.current);
      setFoundInventory(null);
      return;
    }

    playAudio(okAudioEl.current);
    setFoundInventory(inventory);
  }, 300, [productBarcodeInfoValue, matchedInventories]);

  useDebounce(() => {
    if(locationKeyValue) productIdInput.current.focus();
  }, 300, [locationKeyValue]);

  return (
    <Modal isOpen={isOpen}>
      <div className="modal-header">
        <div className="w-100 d-flex justify-content-between">
          <h5 className="modal-title">
            在庫移動
          </h5>
        </div>
      </div>
      <ModalBody>
        <FormGroup>
          <Label>ロケーション</Label>
          <Input value={locationKeyValue} onChange={_ => setLocationKeyValue(_.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>商品バーコード</Label>
          <Input innerRef={productIdInput} value={productBarcodeInfoValue} onChange={_ => setProductBarcodeInfoValue(_.target.value)} />
        </FormGroup>
        {foundInventory && foundProduct && (
          <div className="border rounded p-3 mt-3">
            <div className="mb-2">
              <strong>テナント:</strong> {tenantsById[foundInventory.tenantId]?.name || foundInventory.tenantId}
            </div>
            <div className="mb-2">
              <strong>商品名:</strong> {foundProduct.name}
            </div>
            <div className="mb-2">
              <strong>SKU:</strong> {foundProduct.sku}
            </div>
            <div className="mb-2">
              <strong>バーコード:</strong> {foundInventory.productBarcodeInfo}
            </div>
            <div className="mt-3">
              <ProgressButton
                color="primary"
                className="w-100"
                onClick={handleMoveInventory}
              >
                移動する
              </ProgressButton>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button className="cancel" color="secondary" onClick={onClickClose}>閉じる</Button>
      </ModalFooter>
      <audio src={ngSoundSrc} ref={ngAudioEl} />
      <audio src={okSoundSrc} ref={okAudioEl} />
    </Modal>
  );
};
